import { GridRenderCellParams, useGridApiContext } from '@mui/x-data-grid-pro'
import {
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
  InputAdornment
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import styles from './EditableCell.module.scss'
import { whiteShades } from '@/assets/styles/helpers/colors.styles'

export const EditableTextCell = (params: GridRenderCellParams) => {
  const apiRef = useGridApiContext()

  // Unit shown inside the input (e.g. "zł"), set as a custom colDef property -
  // same pattern as getPlaceholder on the select cell below.
  const { endAdornment } = params.colDef as GridRenderCellParams['colDef'] & {
    endAdornment?: string
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    apiRef.current.updateRows([{ id: params.id, [params.field]: newValue }])
  }

  return (
    <div className={styles.cellWrapper}>
      <TextField
        value={params.value ?? ''}
        onChange={handleChange}
        variant="outlined"
        size="small"
        fullWidth
        className={styles.editableInput}
        slotProps={{
          input: {
            endAdornment: endAdornment ? (
              <InputAdornment position="end">
                <span className={styles.unitAdornment}>{endAdornment}</span>
              </InputAdornment>
            ) : null
          }
        }}
      />
    </div>
  )
}

// Map unit types to translation keys
const unitTypeTranslationKeys: Record<string, string> = {
  weight: 'UNIT_TYPES.WEIGHT',
  volume: 'UNIT_TYPES.VOLUME',
  quantity: 'UNIT_TYPES.QUANTITY',
  pieces: 'UNIT_TYPES.PIECES'
}

export const EditableSelectCell = (params: GridRenderCellParams) => {
  const apiRef = useGridApiContext()
  const { t } = useTranslation()
  const colDefOptions = (params.colDef as any).valueOptions
  const options =
    typeof colDefOptions === 'function'
      ? colDefOptions(params)
      : colDefOptions || []

  // Get placeholder from custom colDef property
  const getPlaceholder = (params.colDef as any).getPlaceholder
  const unitType = getPlaceholder ? getPlaceholder(params) : null
  const placeholder = unitType
    ? t(unitTypeTranslationKeys[unitType] || 'UNIT_TYPES.SELECT')
    : t('UNIT_TYPES.SELECT')

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value
    apiRef.current.updateRows([{ id: params.id, [params.field]: newValue }])
  }

  return (
    <div className={styles.cellWrapper}>
      <Select
        value={params.value ?? ''}
        onChange={handleChange}
        variant="outlined"
        size="small"
        fullWidth
        displayEmpty
        className={styles.editableSelect}
        renderValue={selected => {
          if (!selected) {
            return (
              <span
                className="placeholder-text"
                style={{ color: whiteShades.shade1 }}
              >
                {placeholder}
              </span>
            )
          }
          const selectedOption = options.find(
            (opt: { value: string; label: string }) => opt.value === selected
          )
          return selectedOption?.label || selected
        }}
        MenuProps={{
          className: 'editable-cell-menu'
        }}
      >
        {options.map((opt: { value: string; label: string }) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </div>
  )
}
